import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

/**
 * 팝업창 기본 크기 - 브라우저 창 크기에 비례하되 아래 한계를 넘지 않는다.
 * 계단식 배치가 화면을 벗어나는지 판정할 때도 같은 값을 써야 하므로 함께 내보낸다.
 */
export const POPUP_MAX_WIDTH = 520;
export const POPUP_WIDTH_RATIO = 0.9;
const POPUP_MAX_HEIGHT_RATIO = 0.8;

const PopupWrap = styled.div`
  position: fixed;
  z-index: 900;
  width: ${(props) => props.$width}px;
  max-height: ${(props) => props.$maxHeight}px;
  display: flex;
  flex-direction: column;
  border: 1px solid #e4dbd2;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  overflow: hidden;
`;

const PopupHeader = styled.div`
  position: relative;
  flex: 0 0 auto;
  height: 53px;
  line-height: 53px;
  padding: 0 44px 0 16px;
  background-color: #e4dcd3;
  font-size: 16px;
  font-weight: bold;
  cursor: move;
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseButton = styled.button`
  &&& {
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    padding: 0;
    margin: 0;
    border: 0;
    background: transparent;
    font-size: 18px;
    line-height: 1;
    color: #6b5f54;
    cursor: pointer;
  }
`;

const PopupContents = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const FileArea = styled.div`
  flex: 0 0 auto;
  border-top: 1px solid #eee;
  padding: 10px 16px;

  button {
    display: block;
    width: auto;
    padding: 0;
    margin: 4px 0;
    border: 0;
    background: transparent;
    font-size: 13px;
    text-align: left;
    text-decoration: underline;
    color: #002c5f;
    cursor: pointer;
    word-break: break-all;
  }

  .file_error {
    margin: 6px 0 0;
    font-size: 12.5px;
    color: #c62828;
  }
`;

const PopupFooter = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  border-top: 1px solid #e4dbd2;
  background-color: #faf8f6;
  padding: 10px 16px;

  /*
    전역 CSS가 native 체크박스를 숨기고 인접 label을 체크박스 이미지로 대체한다(index.scss).
    전역 규칙이 label 폭을 16px로 고정하므로, 문구를 담을 수 있도록 같은 선택자 형태로 폭을 되돌린다.
  */
  input[type="checkbox"] + label {
    width: auto;
    padding-left: 22px;
    line-height: 17px;
    font-size: 13px;
    cursor: pointer;
  }
`;

/**
 * 공지사항 팝업창 (화면 내 모달)
 * - 헤더 드래그로 위치 이동, 브라우저 영역을 벗어나지 않도록 제한
 * - 우측 상단 X 아이콘으로 닫기
 * - 다시 보지 않음 체크 후 닫으면 팝업 종료일시까지 재표출하지 않음
 */
const NoticePopup = ({
  notice,
  offset = 0,
  onClose,
  onDownload,
  downloadError = false,
}) => {
  const { t } = useTranslation();
  const wrapRef = useRef(null);
  const dragStateRef = useRef(null);
  const [hideAgain, setHideAgain] = useState(false);

  const width = Math.min(POPUP_MAX_WIDTH, window.innerWidth * POPUP_WIDTH_RATIO);
  const maxHeight = window.innerHeight * POPUP_MAX_HEIGHT_RATIO;

  const [position, setPosition] = useState(() => ({
    left: Math.max(10, (window.innerWidth - width) / 2 + offset),
    top: Math.max(10, window.innerHeight * 0.12 + offset),
  }));

  // 팝업창이 브라우저 영역을 벗어나지 않도록 좌표를 보정한다.
  const clampPosition = useCallback((left, top) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    const popupWidth = rect?.width || 0;
    const popupHeight = rect?.height || 0;

    return {
      left: Math.min(Math.max(0, left), Math.max(0, window.innerWidth - popupWidth)),
      top: Math.min(Math.max(0, top), Math.max(0, window.innerHeight - popupHeight)),
    };
  }, []);

  const handleDragStart = (e) => {
    // 닫기 버튼 클릭은 드래그로 처리하지 않는다.
    if (e.target.closest("button")) {
      return;
    }
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originLeft: position.left,
      originTop: position.top,
    };
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      const dragState = dragStateRef.current;
      if (!dragState) {
        return;
      }
      setPosition(
        clampPosition(
          dragState.originLeft + (e.clientX - dragState.startX),
          dragState.originTop + (e.clientY - dragState.startY)
        )
      );
      e.preventDefault();
    };

    const handleMouseUp = () => {
      dragStateRef.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [clampPosition]);

  // 브라우저 창 크기가 바뀌면 팝업이 화면을 벗어나지 않도록 보정한다.
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev.left, prev.top));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clampPosition]);

  const fileNames = [notice.fileName1, notice.fileName2, notice.fileName3].filter(
    (fileName) => fileName
  );

  return (
    <PopupWrap
      ref={wrapRef}
      $width={width}
      $maxHeight={maxHeight}
      style={{ left: `${position.left}px`, top: `${position.top}px` }}
    >
      <PopupHeader onMouseDown={handleDragStart} title={notice.title}>
        {notice.title}
        <CloseButton
          type="button"
          aria-label={t("L0005", "Cancel")}
          onClick={() => onClose(notice, hideAgain)}
        >
          &#10005;
        </CloseButton>
      </PopupHeader>

      <PopupContents>{notice.content}</PopupContents>

      {fileNames.length > 0 && (
        <FileArea>
          {fileNames.map((fileName) => (
            <button
              key={fileName}
              type="button"
              onClick={() => onDownload(notice.id, fileName)}
            >
              {fileName}
            </button>
          ))}
          {downloadError && <p className="file_error">{t("L0957")}</p>}
        </FileArea>
      )}

      <PopupFooter>
        <input
          type="checkbox"
          id={`notice_popup_hide_${notice.id}`}
          checked={hideAgain}
          onChange={(e) => setHideAgain(e.target.checked)}
        />
        <label htmlFor={`notice_popup_hide_${notice.id}`}>
          {t("L1136", "Do not show again")}
        </label>
      </PopupFooter>
    </PopupWrap>
  );
};

export default NoticePopup;
